import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, X } from 'lucide-react';
import { addSoftwareAssignment, deleteSoftwarePool, getSoftwarePool, getSoftwareSecurityGroup, linkSoftwarePool, listSoftware, revokeSoftwareAssignment, unlinkSoftwarePool, type SoftwareAssignBody } from '@/api/software';
import { getServices, linkServicePool, unlinkServicePool } from '@/api/services';
import { getAudit } from '@/api/audit';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { EntityPicker, type PickerOption } from '@/components/EntityPicker';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { UtilisationBar } from '../Register';

type Tab = 'assignments' | 'utilisation' | 'services' | 'securitygroup' | 'linked' | 'audit';

// Service search for the link picker — the small CMDB catalogue, filtered client-side.
const serviceSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  getServices(signal).then((list) =>
    list
      .filter((s) => s.Name.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 20)
      .map((s) => ({ id: s.ServiceId, label: s.Name, sublabel: s.ServiceGroup ?? undefined })));

export function SoftwareDetail() {
  const { id } = useParams();
  const poolId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canMutate, isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('assignments');
  const [addOpen, setAddOpen] = useState(false);
  const [revokeId, setRevokeId] = useState<number | null>(null);
  const [retireOpen, setRetireOpen] = useState(false);
  const [kind, setKind] = useState<SoftwareAssignBody['AssigneeKind']>('Person');
  const [assignValue, setAssignValue] = useState('');
  const [svcPickerKey, setSvcPickerKey] = useState(0);

  const query = useQuery({ queryKey: ['software', poolId], queryFn: ({ signal }) => getSoftwarePool(poolId, signal) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['software', poolId] });
  const mAdd = useMutation({ mutationFn: (b: SoftwareAssignBody) => addSoftwareAssignment(poolId, b), onSuccess: () => { invalidate(); setAddOpen(false); } });
  const mRevoke = useMutation({ mutationFn: (assignmentId: number) => revokeSoftwareAssignment(poolId, assignmentId), onSuccess: () => { invalidate(); setRevokeId(null); } });
  const mLinkSvc = useMutation({ mutationFn: (serviceId: number) => linkServicePool(serviceId, poolId), onSuccess: () => { setSvcPickerKey((k) => k + 1); invalidate(); } });
  const mUnlinkSvc = useMutation({ mutationFn: (serviceId: number) => unlinkServicePool(serviceId, poolId), onSuccess: invalidate });
  const mRetire = useMutation({ mutationFn: () => deleteSoftwarePool(poolId), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['software'] }); navigate('/software'); } });
  const invalidateAll = () => { queryClient.invalidateQueries({ queryKey: ['software'] }); };
  const mLink = useMutation({ mutationFn: (otherId: number) => linkSoftwarePool(poolId, otherId), onSuccess: () => { setLinkPickerKey((k) => k + 1); invalidateAll(); invalidate(); } });
  const mUnlink = useMutation({ mutationFn: () => unlinkSoftwarePool(poolId), onSuccess: () => { invalidateAll(); invalidate(); } });
  const [linkPickerKey, setLinkPickerKey] = useState(0);

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red">Could not load this pool.</p>;
  const { Pool: pool, Assignments: assignments, Services: services, LinkedPools: linkedPools } = query.data;

  // Pool picker for linking: exclude self + already-linked; small catalogue, filtered client-side.
  const linkedIds = new Set([poolId, ...linkedPools.map((p) => p.SoftwarePoolId)]);
  const poolSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
    listSoftware(signal).then((list) =>
      list
        .filter((p) => !linkedIds.has(p.SoftwarePoolId) && `${p.Title} ${p.SKU ?? ''}`.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 20)
        .map((p) => ({ id: p.SoftwarePoolId, label: p.Title, sublabel: p.SKU ?? undefined })));

  const submitAdd = () => {
    const b: SoftwareAssignBody = { AssigneeKind: kind };
    if (kind === 'Person') b.PersonId = Number(assignValue) || undefined;
    else b.AssetId = Number(assignValue) || undefined;
    mAdd.mutate(b, { onSuccess: () => setAssignValue('') });
  };

  return (
    <section>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Link to="/software" className="text-xs text-navy hover:underline">← Software</Link>
          <h1 className="font-heading text-2xl font-bold text-navy">{pool.Title}</h1>
          <p className="text-sm text-darkGrey/80">
            {pool.Vendor ?? '—'} · {pool.LicenceType}
            {pool.SKU ? ` · SKU (text) ${pool.SKU}` : ''}
            {pool.SkuId != null ? ` · SKU (ID) ${pool.SkuId}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canMutate && (
            <Link to={`/software/${poolId}/edit`} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Edit</Link>
          )}
          {isAdmin && (
            <button type="button" onClick={() => { mRetire.reset(); setRetireOpen(true); }} className="rounded border border-red/40 bg-white px-3 py-1.5 text-sm font-medium text-red hover:bg-red/10">Retire</button>
          )}
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-border text-sm">
        {([['assignments', 'Assignments'], ['utilisation', 'Utilisation'], ['services', 'Services'], ['securitygroup', 'Security group'], ['linked', 'Linked'], ['audit', 'Audit']] as [Tab, string][]).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={tab === k ? 'border-b-2 border-orange px-3 py-2 font-semibold text-navy' : 'px-3 py-2 text-darkGrey/70 hover:text-navy'}>{l}</button>
        ))}
      </div>

      {tab === 'assignments' && (
        <div>
          {canMutate && <button type="button" onClick={() => { mAdd.reset(); setAddOpen(true); }} className="mb-3 rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">Add assignment</button>}
          <ul className="space-y-2 text-sm">
            {assignments.length === 0 && <li className="text-darkGrey/70">No assignments.</li>}
            {assignments.map((a) => (
              <li key={a.AssignmentId} className="flex items-center justify-between rounded border border-border bg-white px-3 py-2">
                <span>
                  {a.AssigneeKind === 'Person' ? (
                    a.PersonName ? (
                      <Link to={`/people/${a.PersonId}`} className="font-medium text-navy hover:underline">{a.PersonName}</Link>
                    ) : (
                      <span className="font-medium text-navy">Person #{a.PersonId}</span>
                    )
                  ) : (
                    <span className="font-medium text-navy">Asset #{a.AssetId}</span>
                  )}
                  {a.AssigneeKind === 'Person' && (a.JobTitle || a.DepartmentCode) && (
                    <span className="text-darkGrey/70">
                      {a.JobTitle ? ` · ${a.JobTitle}` : ''}
                      {a.DepartmentCode ? ` · ${a.DepartmentDescription ? `${a.DepartmentCode} — ${a.DepartmentDescription}` : a.DepartmentCode}` : ''}
                    </span>
                  )}
                  <span className="text-darkGrey/70"> · from {formatDate(a.AssignedDate)}{a.RevokedDate ? ` · revoked ${formatDate(a.RevokedDate)}` : ''}</span>
                </span>
                {canMutate && a.RevokedDate === null && (
                  <button type="button" onClick={() => { mRevoke.reset(); setRevokeId(a.AssignmentId); }} className="text-xs font-semibold text-red hover:underline">Revoke</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'utilisation' && (
        <div className="max-w-md">
          <UtilisationBar pool={pool} />
          <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
            <dt className="font-semibold text-darkGrey">Seats used</dt><dd className="text-darkGrey/80">{pool.SeatsUsed}</dd>
            <dt className="flex items-center gap-2 text-darkGrey"><span className="inline-block h-2 w-2 shrink-0 rounded-full bg-navy" aria-hidden />Mandatory</dt><dd className="text-darkGrey/80">{pool.MandatorySeats}</dd>
            <dt className="flex items-center gap-2 text-darkGrey"><span className="inline-block h-2 w-2 shrink-0 rounded-full bg-orange" aria-hidden />Optional</dt><dd className="text-darkGrey/80">{pool.OptionalSeats}{pool.OptionalSeats > 0 ? ' · reclaimable' : ''}</dd>
            <dt className="flex items-center gap-2 text-darkGrey"><span className="inline-block h-2 w-2 shrink-0 rounded-full bg-darkGrey/40" aria-hidden />Unclassified</dt><dd className="text-darkGrey/80">{pool.UnclassifiedSeats}</dd>
            <dt className="font-semibold text-darkGrey">Total seats</dt><dd className="text-darkGrey/80">{pool.TotalSeats ?? 'Unlimited'}</dd>
            <dt className="font-semibold text-darkGrey">Cost per seat</dt><dd className="text-darkGrey/80">{pool.CostPerSeat ?? '—'}</dd>
            <dt className="font-semibold text-darkGrey">Contract</dt>
            <dd className="text-darkGrey/80">
              {pool.ContractId
                ? <Link to={`/contracts/${pool.ContractId}`} className="text-navy hover:underline">{pool.ContractName ?? `#${pool.ContractId}`}</Link>
                : <span className="text-darkGrey/60">Not linked — add a contract line item for this pool</span>}
            </dd>
          </dl>
          <p className="mt-2 text-xs text-darkGrey/60">Seats and cost per seat are set on the contract line item.</p>
          <p className="mt-1 text-xs text-darkGrey/60">Mandatory seats are dedicated and drive over-allocation; Optional seats are eligible-on-request and reclaimable when idle (BMA only). Unclassified = active seats with no matching department entitlement.</p>
          {pool.OverAllocated && <p className="mt-2 inline-flex rounded-full bg-red/15 px-2 py-0.5 text-xs font-semibold text-red">Over-allocated</p>}
        </div>
      )}

      {tab === 'services' && (
        <div className="max-w-md">
          <p className="mb-2 text-xs text-darkGrey/70">CMDB services this licence pool supports. The pool's licence cost shows against these on the Services page.</p>
          <ul className="space-y-2 text-sm">
            {services.length === 0 && <li className="text-darkGrey/70">Not linked to any service.</li>}
            {services.map((s) => (
              <li key={s.ServiceId} className="flex items-center justify-between rounded border border-border bg-white px-3 py-2">
                <span className="font-medium text-navy">{s.Name}</span>
                {canMutate && (
                  <button type="button" onClick={() => mUnlinkSvc.mutate(s.ServiceId)} disabled={mUnlinkSvc.isPending} aria-label={`Unlink ${s.Name}`} className="rounded p-0.5 text-darkGrey/40 hover:bg-red/10 hover:text-red disabled:opacity-50">
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {canMutate && (
            <div className="mt-3">
              <EntityPicker key={svcPickerKey} queryKey="pool-link-service" search={serviceSearch} minChars={1}
                valueLabel="" onChange={(id) => { if (id) mLinkSvc.mutate(id); }}
                placeholder="Link a service…" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
              {mLinkSvc.isError && <p role="alert" className="mt-1 text-xs text-red">Could not link that service.</p>}
            </div>
          )}
        </div>
      )}

      {tab === 'securitygroup' && <SecurityGroupTab poolId={poolId} canEdit={canMutate} />}

      {tab === 'linked' && (
        <div className="max-w-md">
          <p className="mb-2 text-xs text-darkGrey/70">
            Linked pools form a mirror group: allocating a department to any one of them (and toggling Mandatory / Optional, or removing) applies to all. Use this for licences that always travel together — e.g. a suite and its add-on.
          </p>
          <ul className="space-y-2 text-sm">
            {linkedPools.length === 0 && <li className="text-darkGrey/70">Not linked to any other pool.</li>}
            {linkedPools.map((lp) => (
              <li key={lp.SoftwarePoolId} className="rounded border border-border bg-white px-3 py-2">
                <Link to={`/software/${lp.SoftwarePoolId}`} className="font-medium text-navy hover:underline">{lp.Title}</Link>
              </li>
            ))}
          </ul>
          {isAdmin && linkedPools.length > 0 && (
            <button type="button" onClick={() => mUnlink.mutate()} disabled={mUnlink.isPending} className="mt-2 rounded border border-red/40 bg-white px-3 py-1.5 text-sm font-medium text-red hover:bg-red/10 disabled:opacity-50">
              {mUnlink.isPending ? 'Unlinking…' : 'Unlink this pool from the group'}
            </button>
          )}
          {isAdmin && (
            <div className="mt-3">
              <EntityPicker key={linkPickerKey} queryKey="pool-link-pool" search={poolSearch} minChars={1}
                valueLabel="" onChange={(id) => { if (id) mLink.mutate(Number(id)); }}
                placeholder="Link another pool…" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
              {mLink.isError && <p role="alert" className="mt-1 text-xs text-red">Could not link that pool.</p>}
              <p className="mt-2 text-xs text-darkGrey/60">Linking reconciles both pools' department allocations to the union (Mandatory wins). Unlinking keeps existing allocations.</p>
            </div>
          )}
        </div>
      )}

      {tab === 'audit' && <AuditTab poolId={poolId} />}

      <AlertDialog open={addOpen} onOpenChange={setAddOpen} title="Add assignment" confirmLabel="Add" closeOnConfirm={false} onConfirm={submitAdd}>
        <div className="space-y-2">
          <select aria-label="Assignee kind" value={kind} onChange={(e) => setKind(e.target.value as SoftwareAssignBody['AssigneeKind'])} className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm">
            <option value="Person">Person</option>
            <option value="Asset">Asset</option>
          </select>
          <input aria-label="Assignee id" value={assignValue} onChange={(e) => setAssignValue(e.target.value)} placeholder={kind === 'Person' ? 'Person ID' : 'Asset ID'} className="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          {mAdd.isError && <p role="alert" className="text-xs text-red">{mAdd.error instanceof ApiError ? mAdd.error.message : 'Could not add the assignment.'}</p>}
        </div>
      </AlertDialog>

      <AlertDialog open={revokeId !== null} onOpenChange={(o) => !o && setRevokeId(null)} title="Revoke assignment?" description="Frees the seat (soft revoke)." confirmLabel="Revoke" destructive closeOnConfirm={false} onConfirm={() => { if (revokeId !== null) mRevoke.mutate(revokeId); }}>
        {mRevoke.isError && <p role="alert" className="text-xs text-red">{mRevoke.error instanceof ApiError ? mRevoke.error.message : 'Could not revoke the assignment.'}</p>}
      </AlertDialog>

      <AlertDialog open={retireOpen} onOpenChange={setRetireOpen} title="Retire this licence pool?" description="Hides the pool and removes it from linked services. Assignments and contract lines are kept for audit. This can't be undone from the UI." confirmLabel={mRetire.isPending ? 'Retiring…' : 'Retire'} destructive closeOnConfirm={false} onConfirm={() => mRetire.mutate()}>
        {mRetire.isError && <p role="alert" className="text-xs text-red">{mRetire.error instanceof ApiError ? mRetire.error.message : 'Could not retire the pool.'}</p>}
      </AlertDialog>
    </section>
  );
}

// ── Security group tab ──────────────────────────────────────────────────────

/** Read-only monospace rule block with a copy button. */
function RuleBlock({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-border bg-lightBlue/40 px-3 py-2 pr-20 font-mono text-xs text-darkGrey">{text}</pre>
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy ${label}`}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded border border-border bg-white px-2 py-1 text-xs font-medium text-navy hover:bg-bg"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function SecurityGroupTab({ poolId, canEdit }: { poolId: number; canEdit: boolean }) {
  const query = useQuery({ queryKey: ['software', poolId, 'security-group'], queryFn: ({ signal }) => getSoftwareSecurityGroup(poolId, signal) });
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red">Could not load the security group.</p>;
  const g = query.data;
  const isDynamic = g.MembershipType === 'DynamicMembership';

  return (
    <div className="max-w-2xl space-y-6">
      {/* ── Linked group ── */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-navy">Linked group</h2>
        {g.Linked ? (
          <>
            <dl className="grid grid-cols-[9rem,1fr] gap-y-2 text-sm">
              <dt className="font-semibold text-darkGrey">Group name</dt>
              <dd className="text-darkGrey/80">{g.DisplayName ?? g.SecurityGroupName ?? '—'}</dd>
              <dt className="font-semibold text-darkGrey">Object ID</dt>
              <dd className="break-all font-mono text-xs text-darkGrey/80">{g.SecurityGroupId}</dd>
              {g.GraphError === null && (
                <>
                  <dt className="font-semibold text-darkGrey">Membership</dt>
                  <dd>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${isDynamic ? 'bg-navy/10 text-navy' : 'bg-darkGrey/10 text-darkGrey'}`}>
                      {isDynamic ? 'Dynamic' : 'Assigned'}
                    </span>
                  </dd>
                  <dt className="font-semibold text-darkGrey">Members</dt>
                  <dd className="text-darkGrey/80">{g.MemberCount ?? '—'}</dd>
                </>
              )}
            </dl>
            {g.GraphError === 'unavailable' && (
              <p className="mt-2 rounded border border-orange/30 bg-orange/10 px-3 py-2 text-xs text-darkGrey">
                Live member count and membership rule are unavailable — the Microsoft Graph permission for func-oscar (GroupMember.Read.All + Directory.Read.All) may still be pending. The stored link and suggested rule below are unaffected.
              </p>
            )}
            {g.GraphError === 'not-found' && (
              <p className="mt-2 rounded border border-red/30 bg-red/10 px-3 py-2 text-xs text-red">
                This group object ID wasn't found in Entra. Check the ID on the pool's Edit screen.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-darkGrey/70">
            Not linked to a security group.{' '}
            {canEdit && <Link to={`/software/${poolId}/edit`} className="font-medium text-navy hover:underline">Link one</Link>}
            {' '}to have Azure assign this pool's licences by group membership.
          </p>
        )}
      </section>

      {/* ── The group's existing rule (dynamic groups only) ── */}
      {isDynamic && g.MembershipRule && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-navy">Current membership rule (in Entra)</h2>
          <RuleBlock text={g.MembershipRule} label="current membership rule" />
        </section>
      )}

      {/* ── Suggested rule ── */}
      <section>
        <h2 className="mb-1 text-sm font-semibold text-navy">Suggested dynamic membership rule</h2>
        <p className="mb-2 text-xs text-darkGrey/70">
          Generated from the departments this pool is allocated to, keyed on <code className="font-mono">user.department</code>. This is a starting point — <strong>validate the attribute mapping in Entra</strong> (confirm your directory's <code className="font-mono">department</code> values match these codes) before applying it to a group.
        </p>
        {g.SuggestedRule ? (
          <RuleBlock text={g.SuggestedRule} label="suggested rule" />
        ) : (
          <p className="text-sm text-darkGrey/60">
            No department allocations yet — allocate this pool to departments under Onboarding → Software Allocation to generate a rule.
          </p>
        )}
        {g.Departments.length > 0 && (
          <div className="mt-3">
            <p className="mb-1 text-xs font-semibold text-darkGrey">Contributing departments ({g.Departments.length})</p>
            <ul className="flex flex-wrap gap-1.5">
              {g.Departments.map((d) => (
                <li key={d.Code} className="rounded border border-border bg-white px-2 py-0.5 text-xs" title={d.Description ?? undefined}>
                  <span className="font-mono font-semibold text-navy">{d.Code}</span>
                  {d.Description && <span className="ml-1 text-darkGrey/60">{d.Description}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

function AuditTab({ poolId }: { poolId: number }) {
  const query = useQuery({ queryKey: ['audit', 'SoftwarePool', poolId], queryFn: ({ signal }) => getAudit({ entityType: 'SoftwarePool', entityId: poolId }, signal) });
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load audit history.</p>;
  if (!query.data || query.data.length === 0) return <p className="text-sm text-darkGrey/70">No audit history.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {query.data.map((e) => (
        <li key={e.AuditId} className="rounded border border-border bg-white px-3 py-2">
          <span className="font-semibold text-navy">{e.Action}</span>
          <span className="text-darkGrey/70"> · {formatDateTime(e.ChangedDate)} · {e.ChangedBy}</span>
        </li>
      ))}
    </ul>
  );
}
